import {Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren} from '@angular/core';
import {Place} from '../../../../../admin/modules/admin-places/models/place';
import {AdminPlaceService} from '../../../../../admin/modules/admin-places/services/admin-place.service';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {NbTooltipDirective} from '@nebular/theme';
import {distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';

@Component({
    selector: 'pv-common-mission-address-dialog',
    templateUrl: './common-mission-address-dialog.component.html',
    styleUrls: ['./common-mission-address-dialog.component.scss'],
    standalone: false
})
export class CommonMissionAddressDialogComponent implements OnInit {
  public placeForm: UntypedFormGroup;
  public availablePlaces: any[] | null = null;
  public availablePlacesData: Place[] = [];
  public currentPage = 1;
  public limitItems = 50;
  public globalTooltip = 'Adresse créée en accord avec la direction du parc et vérifiée par Pop Valet.'
  public isLoading = false;
  public allPagesLoaded = false;
  public searchText: string;
  public selectedPlace: Place;
  private destroy$: Subject<void> = new Subject<void>();

  @Input() type: string;
  @Input() entityId: string;
  @Input() placeId: string;
  @Output() submitEvent = new EventEmitter();
  @ViewChildren(NbTooltipDirective) globalTooltipDirective: QueryList<NbTooltipDirective>;

  constructor(private fb: UntypedFormBuilder,
              private adminPlace: AdminPlaceService) { }

  ngOnInit(): void {
    this.initForm();
    this.loadEntityPlaces();
  }

  public initForm() {
    this.placeForm = this.fb.group({
      id: [this.placeId],
      doNotChangeContact: false
    });

    this.placeForm.get('id').valueChanges.pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(id => {
      if (this.availablePlacesData.length === 0) {
        this.adminPlace.getPlacesByEntity(this.entityId, this.placeForm.get('id').value, this.currentPage, this.limitItems).then(res => {
          this.availablePlacesData = res;
          this.updateAvailablePlaces();
          this.placeChanged(id);
        });
      } else {
        this.placeChanged(id);
      }
    });
  }

  public loadEntityPlaces() {
    this.adminPlace.getPlacesByEntity(this.entityId, this.placeForm.get('id').value, this.currentPage, this.limitItems).then(res => {
      this.availablePlacesData = res;
      this.updateAvailablePlaces();
    })
  }

  public loadMorePlaces() {
    if (this.isLoading || this.allPagesLoaded) {
      return;
    }
    this.currentPage++;
    this.getPlacesByEntityAfterEvent();
  }

  public getPlacesByEntityAfterEvent() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.adminPlace.getPlacesByEntity(this.entityId, this.placeForm.get('id').value, this.currentPage, this.limitItems)
      .then(newPlaces => {
        if (newPlaces.length < this.limitItems) {
          this.allPagesLoaded = true;
        }
        const filteredNewPlaces = newPlaces.filter(newPlace =>
          !this.availablePlacesData.some(existingPlace => existingPlace.id === newPlace.id)
        );
        this.availablePlacesData = this.availablePlacesData.concat(filteredNewPlaces);
        this.updateAvailablePlaces();
      })
      .catch(error => {
        console.error('Erreur lors de la récupération des adresses paginées : ', error);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private updateAvailablePlaces() {
    this.availablePlaces = this.availablePlacesData.map(place => ({
      value: place.id,
      name: this.getAutocompletePlaceFullName(place),
      search: this.getAutocompletePlaceSearchText(place),
      customData: { global: place.global, addressesCount: place.addresses.length }
    }));
  }

  private getAutocompletePlaceFullName(place: Place) {
    return (place.title ? place.title + ' ' : '') +
      (place.subName ? place.subName + ' ' : '') +
      (place.name ? place.name + ' ' : '');
  }

  private getAutocompletePlaceSearchText(place: Place) {
    return place.addresses && place.addresses[0]?.label ? place.addresses[0].label : null;
  }

  public onSearchChanged(searchText: string) {
    this.isLoading = false;
    this.allPagesLoaded = false;

    this.searchText = searchText;
    this.currentPage = 1;

    if (this.searchText === '') {
      this.loadEntityPlaces()
    } else {
      this.getPlacesByEntityAfterEvent();
    }
  }

  public async placeChanged(placeId: string) {
    if (!placeId) {
      return;
    }

    this.selectedPlace = this.availablePlacesData.find(p => p.id === placeId);

    if (!this.selectedPlace) {
      this.selectedPlace = await this.adminPlace.getPlace(placeId);
      this.availablePlacesData.push(this.selectedPlace);
      this.updateAvailablePlaces();
    }
  }

  public closeTooltip() {
    this.globalTooltipDirective.forEach(tooltip => tooltip.hide());
  }

  public submit() {
    const place = this.selectedPlace;
    const doNotChangeContact = this.placeForm.get('doNotChangeContact').value;
    this.submitEvent.emit({place, doNotChangeContact});
  }
}
